import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Loader } from '@lms/ui';
import { QuizPlayer, type QuizQuestion } from '@/components/assessments/QuizPlayer';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { withLibraryContext } from '@/lib/library-navigation';

interface PreviewSettings {
  type?: string;
  time_limit_minutes?: number;
}

interface PreviewPayload {
  preview: true;
  assessment: {
    title: string;
    code?: string;
    currentVersion?: { settings?: PreviewSettings | null };
  };
  timeLimitSeconds?: number | null;
  questions: {
    id: string;
    stem: string;
    type?: string;
    currentVersion?: { options?: { id: string; label?: string; content: string }[] };
  }[];
}

const TYPE_LABELS: Record<string, string> = {
  exam: 'Examen',
  quiz: 'Quiz',
  survey: 'Encuesta',
};

const TYPE_PREFIX: Record<string, string> = {
  exam: 'Examen: ',
  quiz: 'Quiz: ',
  survey: 'Encuesta: ',
};

export function AssessmentPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [closed, setClosed] = useState(false);
  const backTo = id ? withLibraryContext(`/evaluaciones/${id}`, searchParams) : '/evaluaciones';

  const { data, isLoading, error } = useQuery({
    queryKey: ['assessment-preview', id],
    queryFn: () => api<PreviewPayload>(`/assessments/${id}/preview`),
    enabled: Boolean(id),
  });

  const settings = data?.assessment?.currentVersion?.settings ?? {};
  const assessmentType = settings.type ?? 'exam';
  const typeLabel = TYPE_LABELS[assessmentType] ?? 'Examen';
  const titlePrefix = TYPE_PREFIX[assessmentType] ?? '';
  const timeLimitSeconds =
    typeof data?.timeLimitSeconds === 'number' && data.timeLimitSeconds > 0
      ? data.timeLimitSeconds
      : typeof settings.time_limit_minutes === 'number' && settings.time_limit_minutes > 0
        ? settings.time_limit_minutes * 60
        : null;

  const questions: QuizQuestion[] =
    data?.questions.map((question, index) => ({
      id: question.id,
      index,
      stem: question.stem,
      type: question.type,
      options: question.currentVersion?.options?.map((option) => ({
        id: option.id,
        label: option.label ?? '',
        content: option.content,
      })),
    })) ?? [];

  return (
    <div className="lms-quiz-attempt font-sans">
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <header className="lms-quiz-attempt__header">
          <HubBackLink to={backTo} label="Volver a la evaluación" />
          <div className="lms-quiz-attempt__header-copy">
            <h1>
              {titlePrefix}
              {data?.assessment?.title ?? 'Vista previa'}
            </h1>
            <p>
              <span className="material-symbols-outlined" aria-hidden="true">
                visibility
              </span>
              Vista previa · {typeLabel} · {data?.assessment?.code ?? '—'}
            </p>
          </div>
        </header>

        <Alert variant="info" className="mb-4" title="Vista previa">
          Así verá la evaluación quien la realice. Las respuestas no se guardan ni cuentan como intento.
        </Alert>

        {error && (
          <Alert variant="danger" className="mb-4">
            {(error as Error).message}
          </Alert>
        )}
        {closed && (
          <Alert variant="success" title="Vista previa cerrada" className="mb-4">
            No se registró ningún intento.
          </Alert>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : data ? (
          <QuizPlayer
            questions={questions}
            timeLimitSeconds={timeLimitSeconds}
            submitted={closed}
            finishLabel="Terminar vista previa"
            submittedLabel="Vista previa cerrada"
            confirmTitle="Terminar vista previa"
            confirmDescription="Las respuestas no se guardan. Esta vista previa no cuenta como intento."
            confirmSubmitLabel="Cerrar vista previa"
            onSubmit={() => setClosed(true)}
          />
        ) : null}
      </div>
    </div>
  );
}
